import { useState } from "react";
import "./ChatRows.css";
import { clockText, deliveryText, trackLabel } from "../chat/api";
import type { LogEntry, Message, TaskEvent } from "../chat/api";
import { STATUS } from "./Status";
import type { StatusKey } from "./Status";
import { tx } from "../i18n";

/* ══ 대화 줄 ══
   말(`role: message`)과 진행(`role: progress`)은 **서버가 접은 값**으로 가른다 — 여기서
   스트림을 다시 읽지 않는다. 원문(`raw`)은 그대로, 「원문」이라 붙여서. 셋의 구분은 색이
   아니라 **들여쓰기 · 글자 크기 · 글리프**다(공통 프레임 Figma 40-2).

   접는 줄의 글리프는 › (접힘) · ⌄ (펼침). 이 프로젝트의 글꼴에 ▸ ▾ 가 없다(b3x-pr 실측). */

const KIND_KO: Record<string, string> = {
  prompt: tx("chat.kind.prompt"),
  message: tx("chat.kind.message"),
  system: tx("chat.kind.system"),
  warning: tx("chat.kind.warning"),
  activity: tx("chat.kind.activity"),
  thinking: tx("chat.kind.thinking"),
  tool_result: tx("chat.kind.toolResult"),
  stderr: tx("chat.kind.stderr"),
  raw: tx("chat.kind.raw"),
};

function kindKo(kind: string): string {
  return KIND_KO[kind] ?? kind;
}

function charCount(s: string): string {
  return tx("chat.chars", { n: s.length.toLocaleString() });
}

/** 접히는 줄 하나 — 접혀도 **이름과 한 줄 요약은 보인다.** 펼칠 것이 없으면 단추가 아니다. */
function Fold({
  glyph,
  head,
  summary,
  body,
  tone,
  defaultOpen = false,
}: {
  glyph: string;
  head: string;
  summary?: string | null;
  body?: string | null;
  tone?: "error" | "muted";
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const canOpen = Boolean(body && body.trim());
  const cls = `crow crow--prog${tone === "error" ? " crow--error" : ""}`;
  const headLine = (
    <>
      <span className="crow__fold" aria-hidden="true">
        {canOpen ? (open ? "⌄" : "›") : "·"}
      </span>
      <span className="crow__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="crow__head">{head}</span>
      {summary ? <span className="crow__sum">{summary}</span> : null}
    </>
  );
  return (
    <div className={cls}>
      {canOpen ? (
        <button type="button" className="crow__btn" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {headLine}
        </button>
      ) : (
        <div className="crow__btn crow__btn--static">{headLine}</div>
      )}
      {open && canOpen ? <pre className="crow__body">{body}</pre> : null}
    </div>
  );
}

/** 세션 로그 한 줄. */
export function LogRow({ e, who }: { e: LogEntry; who: string }) {
  if (e.role === "message") {
    if (e.kind === "prompt") {
      return (
        <Fold glyph="▣" head={tx("chat.kind.prompt")} summary={tx("chat.promptSummary", { chars: charCount(e.text) })} body={e.text} tone="muted" />
      );
    }
    return (
      <div className={`crow crow--msg${e.isError ? " crow--error" : ""}`}>
        <div className="crow__from">
          {who}
          {e.kind !== "message" ? ` · ${kindKo(e.kind)}` : ""}
          {e.isError ? ` · ${tx("ui.alertHead.error")}` : ""}
        </div>
        <div className="crow__text">{e.text}</div>
      </div>
    );
  }
  if (e.role === "raw") {
    return <Fold glyph="≡" head={tx("chat.kind.raw")} summary={e.text} body={e.text} tone="muted" />;
  }
  // progress — 도구·생각·결과·표준오류. 접혀도 이름과 요약은 보인다.
  const label = e.label ?? kindKo(e.kind);
  const glyph = e.isError ? "✕" : e.kind === "tool_result" ? "↳" : e.kind === "thinking" ? "…" : "›";
  return (
    <Fold
      glyph={glyph}
      head={`${kindKo(e.kind)}${e.label ? ` · ${label}` : ""}${e.isError ? ` · ${tx("ui.alertHead.error")}` : ""}`}
      summary={e.summary ?? (e.body ? null : e.text)}
      body={e.body ?? (e.summary ? e.text : null)}
      tone={e.isError ? "error" : undefined}
    />
  );
}

/** 사람이 남긴 말 — 오른쪽, 시각과 **전달 상태를 글자로**. 접힌 로그 줄엔 시각이 없어
 *  대화 사이에 끼우지 않고 끝에 붙인다(순서를 지어내지 않는다). */
export function HumanRow({ m }: { m: Message }) {
  const d = deliveryText(m);
  return (
    <div className="crow crow--human">
      <div className="crow__bubble">
        <div className="crow__text">{m.body}</div>
        <div className={`crow__deliv crow__deliv--${d.tone}`}>
          <span aria-hidden="true">{d.glyph}</span> {clockText(m.createdAt)} · {m.createdBy} · {d.text}
        </div>
      </div>
    </div>
  );
}

const EV_KO: Record<string, string> = {
  status: tx("field.status"),
  link: tx("chat.event.link"),
  message: tx("chat.event.message"),
  receive: tx("chat.event.receive"),
  "session-open": tx("chat.event.sessionOpen"),
  "session-close": tx("chat.event.sessionClose"),
  stop: tx("chat.stop"),
};

function statusKo(s: string | null): string {
  if (!s) return "—";
  return (STATUS as Record<string, { label: string }>)[s as StatusKey]?.label ?? s;
}

/** 발자국 한 줄 — 사람 세션(Roo Code)의 대화 자리. 모르는 종류는 이름 그대로 그린다. */
export function FootprintRow({ e }: { e: TaskEvent }) {
  const isStatus = e.kind === "status";
  const glyph = isStatus ? "◇" : e.kind === "stop" ? "■" : "·";
  return (
    <div className="crow crow--foot">
      <span className="crow__glyph" aria-hidden="true">
        {glyph}
      </span>
      <span className="crow__head">{EV_KO[e.kind] ?? e.kind}</span>
      <span className="crow__sum crow__sum--wrap">
        {isStatus ? `${statusKo(e.from)} → ${statusKo(e.to)}${e.summary ? ` — ${e.summary}` : ""}` : e.summary}
      </span>
      <span className="crow__when">
        {e.actor} · {clockText(e.at)}
      </span>
    </div>
  );
}

/** 세션 머리 한 줄 — 어느 단계 · 어떻게 끝났나(`note` 그대로). */
export function SessionNote({ track, note }: { track: string | null; note: string | null }) {
  if (!note) return null;
  return (
    <div className="crow crow--note">
      <span className="crow__head">{tx("chat.sessionEnd", { track: trackLabel(track) })}</span>
      <span className="crow__sum crow__sum--wrap">{note}</span>
    </div>
  );
}
